import { formatLocalDate, formatLocalDateTime } from '../utils/localDateTime';
import { fullWorkspaceAccess, isClientView } from '../common/organizationAccessMode';
import { confirmApp, showAppAlert } from '../common/appDialog';

import React, { useState, useEffect } from 'react';
import type { SrmProject, Page, NavParams, AnalysisOrPlanResult, ActiveFramework, FrameworkComponent, Organization, SrmAlert, RiskEntry, SrmModule } from '@types';
import { createSafeMarkdown } from '../utils/safeMarkdown';
import { ICONS, SUITE_DETAILS, SUITE_WORKFLOWS } from './constants';
import { db, auth } from '../../firebaseConfig'; 
import { 
    collection, 
    query, 
    where, 
    getDocs, 
    doc, 
    getDoc, 
    setDoc,
    updateDoc,
    deleteDoc,
    limit,
    onSnapshot
} from 'firebase/firestore';
import Loader from './Loader';
import SrmModuleReviewModal from './SrmModuleReviewModal';
import SrmRiskRegister from './SrmRiskRegister';
import { GlobalLiveNewsGrid } from './GlobalLiveNewsGrid';
import { organizationCoverageCountries } from '../utils/liveNewsRegions';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { sanitizeFirestoreData } from './reports/ReportShared';
import { generateSrmConclusionReport } from '../utils/pdfGenerator';
import { FrameworkDecommissionModal } from './FrameworkDecommissionModal';
import { linkedSrmRemediationModule, reviewedSrmRiskScore, safeSrmDashboardTab, srmDashboardTabs, srmModuleData, type SrmDashboardTab } from '../common/srmClientView';
import { frameworkRequestContext, storeFrameworkRequest } from '../common/srmServiceRequest';
import { cleanSrmSummary } from '../common/srmSummary';
import { isClientSrmSetupProject, SRM_THREAT_LEASE_MS } from '../../functions/src/common/srmThreatAnalysis';
import { isSrmAutomatedWorkflow, srmModuleReportGaps } from '../../functions/src/common/srmModuleReports';

const md = createSafeMarkdown({
    html: true,
    linkify: true,
    typographer: true
});

const EmailReportModal: React.FC<{ 
    project: SrmProject, 
    activeFramework: ActiveFramework | null,
    onClose: () => void 
}> = ({ project, activeFramework, onClose }) => {
    const [recipient, setRecipient] = useState('');
    
    // Use either the active framework components or the project modules
    const components = activeFramework?.components || project.modules || [];
    const totalComponents = components.length;
    const activeComponents = components.filter(c => c.status === 'Active' || c.status === 'Ready').length;
    const compliance = Math.round((activeComponents / totalComponents) * 100) || 0;
    
    const criticalActions = activeFramework?.components.flatMap(c => c.actionItems || [])
        .filter(a => a.status === 'pending' && a.priority === 'Critical') || [];

    const emailSubject = `SRM Status Report: ${project.name || project.projectName} - ${formatLocalDate(new Date())}`;
    const emailBody = `
EXECUTIVE SECURITY SUMMARY
Project: ${project.name || project.projectName}
Date: ${formatLocalDate(new Date())}

CURRENT POSTURE
------------------------------------------------
Overall Compliance: ${compliance}%
Active Risk Score: ${activeFramework?.overallRiskScore || 0}
Components Monitored: ${totalComponents}

CRITICAL ACTIONS REQUIRED
------------------------------------------------
${criticalActions.length > 0 ? criticalActions.map(a => `[ ] ${a.task} (Priority: Critical)`).join('\n') : 'No critical actions pending.'}

SYSTEM STATUS
------------------------------------------------
${components.map(c => `${c.title}: ${c.status}`).join('\n')}

Generated by IntelOptics Insights
    `.trim();

    const handleSend = () => {
        window.location.href = `mailto:${recipient}?subject=${encodeURIComponent(emailSubject)}&body=${encodeURIComponent(emailBody)}`;
        onClose();
    };

    return (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4 animate-fade-in">
            <div className="bg-white rounded-3xl shadow-2xl w-full max-w-lg p-8 border border-slate-200">
                <div className="flex justify-between items-center mb-6 border-b border-slate-100 pb-4">
                    <h3 className="text-xl font-black text-slate-900 uppercase tracking-tight">Generate Executive Brief</h3>
                    <button onClick={onClose} className="text-slate-400 hover:text-slate-600 transition-colors">
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
                    </button>
                </div>
                <p className="text-sm text-slate-500 mb-6 font-medium">
                    This will open your default email client with a pre-formatted strategic status report for project stakeholders.
                </p>
                
                <div className="space-y-6">
                    <div>
                        <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2">Recipient Email Address</label>
                        <input 
                            type="email" 
                            value={recipient}
                            onChange={e => setRecipient(e.target.value)}
                            className="w-full p-4 bg-slate-50 border border-slate-100 rounded-2xl focus:ring-2 focus:ring-blue-500 outline-none transition-all font-bold text-slate-800"
                            placeholder="stakeholder@enterprise.com"
                        />
                    </div>
                    <div>
                        <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2">Strategic Preview</label>
                        <textarea 
                            readOnly 
                            value={emailBody} 
                            className="w-full p-4 bg-slate-50 border border-slate-100 rounded-2xl text-xs font-mono h-48 text-slate-600 overflow-y-auto custom-scrollbar"
                        />
                    </div>
                    <button 
                        onClick={handleSend}
                        className="w-full bg-blue-600 text-white font-black py-4 rounded-2xl hover:bg-blue-700 transition-all shadow-xl shadow-blue-100 uppercase tracking-widest text-xs"
                    >
                        Execute & Open Email Client
                    </button>
                </div>
            </div>
        </div>
    );
};

interface SrmProjectDashboardProps {
    project: SrmProject;
    organization: Organization;
    onNavigate: (nav: { page: Page; params?: NavParams }) => void;
    onBack: () => void;
    onDeploy?: (framework: ActiveFramework) => void;
    onUpdateModule?: (projectId: string, moduleId: string, resultData: any) => Promise<void>;
    navParams?: NavParams;
    readOnly?: boolean;
}

const BACKGROUND_TOOLS = [
    'country_geopolitical_analysis',
    'integrated_threat_analysis',
];

const SrmProjectDashboard: React.FC<SrmProjectDashboardProps & { projectId?: string }> = ({ 
    project: initialProject, 
    onNavigate, 
    onUpdateModule,
    projectId,
    onBack,
    onDeploy,
    organization,
    navParams,
    readOnly = false
}) => {
    const [project, setProject] = useState<SrmProject | null>(initialProject || null);
    const [reviewModuleId, setReviewModuleId] = useState<string | null>(null);
    const [remediationRisk, setRemediationRisk] = useState<RiskEntry | null>(null);
    const [isLoading, setIsLoading] = useState(!initialProject && !!projectId);
    const [alerts, setAlerts] = useState<SrmAlert[]>([]);
    const [latestIntelligence, setLatestIntelligence] = useState<any>(null);
    const [activeTab, setActiveTab] = useState<SrmDashboardTab>(() => safeSrmDashboardTab(navParams?.activeTab, readOnly));
    const [activeFramework, setActiveFramework] = useState<ActiveFramework | null>(null);
    const [showEmailModal, setShowEmailModal] = useState(false);
    const [approvalToast, setApprovalToast] = useState<{ count: number } | null>(null);
    const [isConcluding, setIsConcluding] = useState(false);
    const [showArchiveConfirm, setShowArchiveConfirm] = useState(false);
    const [isArchiving, setIsArchiving] = useState(false);
    const [showDecommissionModal, setShowDecommissionModal] = useState(false);
    const [regeneratingModuleId, setRegeneratingModuleId] = useState<string | null>(null);
    const [regenerationError, setRegenerationError] = useState('');

    useEffect(() => {
        setActiveTab(current => safeSrmDashboardTab(current, readOnly));
    }, [readOnly]);

    // Sync local state when prop changes (e.g. from parent Firestore sync)
    useEffect(() => {
        if (initialProject) {
            setProject(initialProject);
        }
    }, [initialProject]);

    useEffect(() => {
        if (!project && projectId) {
            setIsLoading(true);
            const fetchProject = async () => {
                try {
                    const docRef = doc(db, 'srm_projects', projectId);
                    const docSnap = await getDoc(docRef);
                    if (docSnap.exists()) {
                        setProject({ ...docSnap.data(), id: docSnap.id } as SrmProject);
                    }
                } catch (err) {
                    console.error("Error fetching project:", err);
                } finally {
                    setIsLoading(false);
                }
            };
            fetchProject();
        }
    }, [projectId, project]);

    useEffect(() => {
        if (!project?.id || !organization?.id || !db) return;

        const frameworkQuery = query(
            collection(db, 'active_frameworks'),
            where('organizationId', '==', organization.id),
            where('projectId', '==', project.id),
            limit(1)
        );

        const unsubFramework = onSnapshot(frameworkQuery, (snap) => {
            if (!snap.empty) {
                const docSnap = snap.docs[0];
                setActiveFramework({ id: docSnap.id, ...docSnap.data() } as ActiveFramework);
            } else {
                setActiveFramework(null);
            }
        }, (error) => {
            console.error('SRM framework listener failed:', error);
            setActiveFramework(null);
        });

        return () => unsubFramework();
    }, [project?.id, organization?.id]);

    useEffect(() => {
        if (!project || !db) return;


        const fetchIntelligence = async () => {
            if (!project?.id || (!project.iso3 && !project.locationName)) return;
            
            try {
                // 1. Check for Localized Intelligence first (New Collection)
                const localDocRef = doc(db, 'srm_local_intelligence', project.id);
                const localSnap = await getDoc(localDocRef);
                
                let iso3 = project.iso3?.toUpperCase() || "";
                let countryName = project.locationName || "";
                
                if (localSnap.exists()) {
                    const localData = localSnap.data();
                    const generatedAt = localData.generatedAt?.toDate?.() || new Date(localData.generatedAt);
                    const now = new Date();
                    const hoursSinceGeneration = (now.getTime() - generatedAt.getTime()) / (1000 * 60 * 60);
                    
                    if (hoursSinceGeneration < 24) {
                        console.log("✅ Using cached Localized Intelligence (less than 24h old)");
                        setLatestIntelligence({ id: localSnap.id, ...localData.resultData });
                        
                        // Process alerts from local data if available
                        if (localData.resultData?.recentEvents) {
                            const localAlerts: SrmAlert[] = localData.resultData.recentEvents.map((event: any, idx: number) => ({
                                id: `local-alert-${idx}-${Date.now()}`,
                                timestamp: event.date || new Date().toISOString(),
                                level: (event.severity === 'Critical' || event.severity === 'High') ? 'Critical' : 'Warning',
                                title: event.title || 'Local Intelligence Alert',
                                message: event.description || 'A localized security event has been detected.',
                                source: 'Local AI Analyst',
                                status: 'Active'
                            }));
                            setAlerts(localAlerts);
                        }
                        return; // 🛑 EXIT: Local data is fresh
                    }
                }

                // 2. Localized Report Generation Trigger (If no fresh local report)
                const targetAddress = [project.locationName, project.locationAddress, project.country].filter(Boolean).join(', ');
                
                if (!readOnly && targetAddress && targetAddress.length > 5) {
                    console.log(`[SRM Intel] Triggering NEW Localized Intelligence for: ${targetAddress}`);
                    try {
                        const functions = getFunctions(undefined, 'us-central1');
                        const getAnalysis = httpsCallable(functions, 'onDemandAnalysisProxy');

                        // B2 FIX: Inject full project SRM context so the AI produces project-specific output
                        const srmAssets = (project.modules || [])
                            .filter(m => !BACKGROUND_TOOLS.includes(m.workflowId || ''))
                            .map(m => m.title)
                            .join(', ');

                        const result = await getAnalysis({
                            analysisType: 'Local Strategic Intelligence',
                            formData: {
                                location_country: project.country || project.locationName,
                                location_city: project.locationName,
                                location_address: project.locationAddress,
                                lat: project.lat || 0,
                                lng: project.lng || 0,
                                iso3: project.iso3,
                                // SRM project context for project-specific intelligence grounding
                                srmProjectName: project.name || project.projectName || '',
                                srmProjectScope: project.summary || '',
                                srmAssets: srmAssets || 'No modules configured yet',
                            }
                        });

                        const resultData = (result.data as any)?.result;
                        if (resultData) {
                            // Save to Firestore for 24h caching
                            await setDoc(localDocRef, {
                                projectId: project.id,
                                targetAddress,
                                resultData: sanitizeFirestoreData(resultData),
                                generatedAt: new Date(),
                                iso3: project.iso3
                            });
                            
                            setLatestIntelligence(resultData);
                            console.log("✅ Localized Intelligence generated and persisted.");
                            return; // 🛑 EXIT: Success
                        }
                    } catch (genErr) {
                        console.error("⚠️ Localized Generation failed, falling back to country data:", genErr);
                    }
                }

                // 3. Fallback: Global Daily Country Analysis
                if (!iso3 && countryName.length === 3) {
                    iso3 = countryName.toUpperCase();
                }

                console.log(`[SRM Intel] Fallback: Fetching Country Intelligence for ISO3: ${iso3}`);

                let intelDoc: any = null;
                if (iso3) {
                    const qIso = query(collection(db, 'daily_country_analysis'), where('iso3', '==', iso3));
                    const snapIso = await getDocs(qIso);
                    if (!snapIso.empty) {
                        intelDoc = { id: snapIso.docs[0].id, ...snapIso.docs[0].data() };
                    }
                }

                if (!intelDoc && countryName) {
                    const qCountry = query(collection(db, 'daily_country_analysis'), where('country', '==', countryName));
                    const snapCountry = await getDocs(qCountry);
                    if (!snapCountry.empty) {
                        intelDoc = { id: snapCountry.docs[0].id, ...snapCountry.docs[0].data() };
                    }
                }

                if (intelDoc) {
                    setLatestIntelligence(intelDoc);
                    console.log("✅ Fallback: Country Intelligence synced.");
                    
                    const newAlerts: SrmAlert[] = (intelDoc.recentEvents || []).map((event: any, idx: number) => ({
                        id: `alert-${intelDoc.iso3 || 'global'}-${idx}-${Date.now()}`,
                        timestamp: event.date || new Date().toISOString(),
                        level: (event.severity === 'Critical' || event.severity === 'High') ? 'Critical' : 'Warning',
                        title: event.title || 'Regional Intelligence Alert',
                        message: event.description || 'A significant event has been detected in the project region.',
                        source: 'Daily Analytics Engine',
                        status: 'Active'
                    }));
                    setAlerts(newAlerts);
                } else {
                    console.warn("⚠️ No intelligence found (Local or Country) for this location.");
                }
            } catch (err) {
                console.error("❌ Intelligence sync failed:", err);
            }
        };

        fetchIntelligence();
    }, [project?.id, project?.locationName, project?.iso3, readOnly]);

    useEffect(() => {
        if (readOnly || !project || !alerts.length || !db) return;
        
        let hasNew = false;
        const currentRisks = [...(project.risks || [])];
        
        alerts.forEach(alert => {
            // Check if threat already exists as a risk
            if (!currentRisks.find(r => r.threat === alert.title)) {
                currentRisks.unshift({
                    id: `R-INTEL-${alert.id.split('-').pop() || Date.now()}`,
                    threat: alert.title,
                    category: 'Intelligence',
                    likelihood: alert.level === 'Critical' ? 'High' : 'Medium',
                    impact: 'High',
                    score: alert.level === 'Critical' ? 20 : 12,
                    mitigation: alert.message,
                    owner: 'Intelligence Engine',
                    status: 'Pending' as any,
                    createdAt: new Date().toISOString()
                });
                hasNew = true;
            }
        });

        if (hasNew) {
            handleUpdateRisks(currentRisks);
        }
    }, [alerts, project?.id, readOnly]);

    if (isLoading) return <Loader message="Connecting to SRM Blueprint..." />;
    if (!project) return <div className="p-8 text-center text-slate-500">Project not found or connection lost.</div>;

    // --- Metrics & Data Processing ---
    const allModules = project.modules || [];
    const components = activeFramework?.components || allModules;
    const interactiveModules = allModules.filter(m => !BACKGROUND_TOOLS.includes(m.workflowId || ''));
    const backgroundModules = allModules.filter(m => BACKGROUND_TOOLS.includes(m.workflowId || ''));
    
    const totalModules = interactiveModules.length;
    const isModuleReady = (module: SrmModule) => {
        const data = srmModuleData(module, activeFramework?.components);
        return ['Ready', 'Active'].includes(module.status) && Boolean(data) && !srmModuleReportGaps({ ...module, resultData: data }).length;
    };
    const readyModules = interactiveModules.filter(isModuleReady).length;
    const complianceScore = totalModules > 0 ? Math.round((readyModules / totalModules) * 100) : 0;
    const riskScore = reviewedSrmRiskScore(project.risks || []);

    const projectActions = interactiveModules.filter(module => !isModuleReady(module)).map(m => ({
        id: `project-action-${m.id}`,
        task: `Complete ${m.title} Analysis`,
        priority: 'High' as const,
        status: 'pending' as const,
        componentId: m.id,
        componentName: m.title,
        isProjectModule: true
    }));

    const frameworkActions = activeFramework?.components.flatMap(c => (c.actionItems || []).map(a => ({ ...a, componentId: c.id, componentName: c.title, isProjectModule: false }))) || [];
    
    const riskActions = (project.risks || [])
        .filter(r => r.status === 'Identified' || r.status === 'Active Process')
        .map(r => ({
            id: `risk-action-${r.id}`,
            task: r.status === 'Identified' ? `Initialize Mitigation: ${r.threat}` : `Execute Technical Remediation: ${r.threat}`,
            priority: (r.impact === 'Critical' || r.likelihood === 'Critical') ? 'Critical' : (r.impact === 'High' ? 'High' : 'Medium') as any,
            status: (r.status === 'Active Process' ? 'in-progress' : 'pending') as any,
            componentId: r.targetComponentId || 'srm_risk_register',
            componentName: `RISK: ${r.category}`,
            isProjectModule: false,
            isRisk: true,
            riskId: r.id
        }));

    const allActions = [...projectActions, ...frameworkActions, ...riskActions]
        .sort((a, b) => {
            const priorityMap: Record<string, number> = { 'Critical': 0, 'High': 1, 'Medium': 2, 'Low': 3 };
            return (priorityMap[a.priority as string] || 99) - (priorityMap[b.priority as string] || 99);
        });

    const pendingActions = allActions.filter(a => a.status === 'pending' || a.status === 'in-progress' || (a.status as string) === 'Draft');
    const criticalActions = pendingActions.filter(a => a.priority === 'Critical' || a.priority === 'High');

    const handleRerunTool = (component: SrmModule | FrameworkComponent) => {
        if (readOnly)
            return;
        const workflowId = (component as any).workflowId || (component as any).type;
        let targetSuiteId = component.suiteId || 'srm';
        
        console.log(`[SRM_DEBUG] [handleRerunTool] Intent: Rerunning ${component.title}`, {
            moduleId: component.id,
            workflowId,
            currentSuiteId: component.suiteId
        });

        // Ensure accurate suite routing regardless of legacy tags
        for (const [sId, workflows] of Object.entries(SUITE_WORKFLOWS)) {
            if (workflows.some(w => w.id === workflowId)) {
                targetSuiteId = sId;
                break;
            }
        }

        const suiteName = SUITE_DETAILS[targetSuiteId]?.name || 'Strategic Risk Management';
        
        // Find the matching module in our predefined list to get the canonical workflowId if needed
        const resolvedSrmModule = allModules.find(m => m.id === component.id || (m.suiteId === component.suiteId && m.title === component.title));
        
        const navPayload = { 
            page: 'suiteDashboard' as const, 
            params: { 
                suiteName, 
                activeWorkflowId: workflowId || resolvedSrmModule?.workflowId,
                srmProject: project,
                projectContext: project, // B4 FIX: alias required by GlobalThreatGroupDashboard & other tools
                srmModule: (component as SrmModule),
                targetModuleId: component.id,
                activeTab: activeTab // Preserve tab state for return
            } 
        };

        console.log(`[SRM_DEBUG] [handleRerunTool] Navigation Resolution:`, {
            targetSuite: suiteName,
            targetWorkflow: navPayload.params.activeWorkflowId,
            projectId: project?.id,
            returnTab: activeTab
        });

        onNavigate(navPayload);
    };

    const handleDeployClick = async () => {
        if (readOnly)
            return;
        if (!onDeploy || !organization) {
            console.warn(`[SRM_DEBUG] Deploy failed: onDeploy or organization missing`, { hasOnDeploy: !!onDeploy, hasOrg: !!organization });
            return;
        }
        
        console.log(`[SRM_DEBUG] handleDeployClick initiated for project:`, project.id);

        const incompleteModules = interactiveModules.filter(module => !isModuleReady(module));
        if (incompleteModules.length > 0) {
            console.log(`[SRM_DEBUG] Deploy Warning: ${incompleteModules.length} modules incomplete.`);
            if (!await confirmApp(`DEPLOYMENT WARNING:\n\n${incompleteModules.length} framework component(s) are still in 'Draft' status.\n\nDeploying now will create an active framework with incomplete data. Continue?`)) return;
        }
        
        const now = new Date().toISOString();
        const displayName = project.name || project.projectName || 'Untitled Project';

        const newFramework: ActiveFramework = {
            id: `framework-${Date.now()}`,
            name: `${displayName} Framework`,
            description: cleanSrmSummary(project.summary),
            organizationId: organization.id,
            projectId: project.id,
            deployedAt: now,
            lastReview: now,
            components: allModules.map((m): FrameworkComponent => ({
                id: m.id,
                title: m.title,
                suiteId: m.suiteId,
                type: m.workflowId || 'srm_framework_builder',
                status: m.status === 'Ready' ? 'Active' : 'Review Needed',
                lastUpdated: new Date().toISOString(),
                riskScore: 0, 
                actionItems: [],
                data: m.resultData || {},
            })),
            overallRiskScore: riskScore ?? 0,
            auditLog: [{
                id: `log-${Date.now()}`,
                timestamp: now,
                user: auth.currentUser?.email || 'System',
                action: 'Framework Deployed',
                details: `Initial deployment from project "${displayName}"`
            }],
            risks: project.risks || [],
            alerts: alerts
        };

        console.log(`[SRM_DEBUG] New Framework Object created:`, newFramework.id);

        try {
            if (db) {
                console.log(`[SRM_DEBUG] Updating project status to Active in Firestore`);
                await updateDoc(doc(db, 'srm_projects', project.id), { status: 'Active' });
            }
            console.log(`[SRM_DEBUG] Invoking onDeploy callback`);
            onDeploy(newFramework);
        } catch (err) {
            console.error("[SRM_DEBUG] Deployment failed in Firestore, proceeding with callback anyway:", err);
            onDeploy(newFramework);
        }
    };

    const handleDeleteModule = async (moduleId: string) => {
        if (readOnly)
            return;
        if (!await confirmApp("Remove this component from the blueprint?")) return;
        const updatedModules = allModules.filter(m => m.id !== moduleId);
        try {
            // B3 FIX: Update the srm_projects document (source of truth)
            if (db) await updateDoc(doc(db, 'srm_projects', project.id), { modules: updatedModules });
            // B3 FIX: Cascade delete to active_frameworks so both documents stay in sync
            if (db && activeFramework?.id) {
                const updatedComponents = (activeFramework.components || []).filter(c => c.id !== moduleId);
                await updateDoc(doc(db, 'active_frameworks', activeFramework.id), { components: updatedComponents });
            }
            setProject({ ...project, modules: updatedModules });
        } catch (e) { console.error(e); }
    };

    const handleUpdateRisks = async (updatedRisks: RiskEntry[]) => {
        if (readOnly || !project?.id || !db) return;
        
        // Filter out pending risks from the scoring calculus
        const activeRisks = updatedRisks.filter(r => r.status !== 'Pending');

        // Recalculate overall score based on top 5 active risks
        const sortedScores = [...activeRisks]
            .map(r => r.score || 0)
            .sort((a, b) => b - a);
        const top5 = sortedScores.slice(0, 5);
        const newScore = top5.length > 0 ? Math.round(top5.reduce((a, b) => a + b, 0) / top5.length) : 0;

        try {
            const docRef = doc(db, 'srm_projects', project.id);
            await updateDoc(docRef, { 
                risks: updatedRisks,
                overallRiskScore: newScore,
                updatedAt: new Date().toISOString()
            });
            setProject(prev => prev ? { ...prev, risks: updatedRisks, overallRiskScore: newScore } : null);
        } catch (err) {
            console.error("Error updating risks:", err);
        }
    };

    const handleClientRiskLifecycle = async (riskId: string, status: RiskEntry['status']) => {
        if (!readOnly || !project?.id || !organization?.id)
            return;
        const updateRisk = httpsCallable(getFunctions(undefined, 'us-central1'), 'updateClientSrmRisk');
        await updateRisk({ organizationId: organization.id, projectId: project.id, riskId, status });
        setProject(current => current ? { ...current, risks: current.risks?.map(risk => risk.id === riskId
            ? { ...risk, status, updatedAt: new Date().toISOString() }
            : risk) } : null);
    };

    const moduleData = (module: SrmModule) => srmModuleData(module, activeFramework?.components);

    const canRegenerate = (!readOnly && fullWorkspaceAccess || !fullWorkspaceAccess && !isClientView &&
        isClientSrmSetupProject(project, organization.id, auth.currentUser?.uid || '')) &&
        !['Archived', 'Concluded', 'Completed'].includes(project?.status || '');
    const isRegenerating = (module: SrmModule) => regeneratingModuleId === module.id ||
        ((module as any).reportGeneration?.status === 'generating' && (module as any).reportGeneration.startedAt > Date.now() - SRM_THREAT_LEASE_MS);
    const regenerateThreatAnalysis = async (module: SrmModule) => {
        if (!canRegenerate || !isSrmAutomatedWorkflow(module.workflowId) || regeneratingModuleId || isRegenerating(module))
            return;
        setRegeneratingModuleId(module.id);
        setRegenerationError('');
        try {
            const regenerate = httpsCallable(getFunctions(undefined, 'us-central1'), 'regenerateSrmThreatAnalysis', { timeout: 600000 });
            const response = await regenerate({ organizationId: organization.id, projectId: project.id, moduleId: module.id });
            const report = (response.data as any).report;
            setProject(current => current ? { ...current, modules: current.modules.map(candidate => candidate.id === module.id
                ? { ...candidate, resultData: report, status: srmModuleReportGaps({ ...candidate, resultData: report,
                    reportGeneration: { status: 'ready' } }).length ? 'Review Needed' : 'Ready', reportGeneration: { status: 'ready' } } : candidate) } : current);
        } catch (error: any) {
            setRegenerationError(error.message || 'Regeneration failed. The previous report has been retained.');
        } finally {
            setRegeneratingModuleId(null);
        }
    };

    const requestModuleService = (module?: SrmModule | FrameworkComponent, action?: string) => {
        try {
            const context = frameworkRequestContext(project, module, action);
            const frameworkRequestId = storeFrameworkRequest(sessionStorage, organization.id, context);
            onNavigate({ page: 'serviceRequests', params: { serviceCode: context.serviceCode, frameworkRequestId } });
        } catch {
            void showAppAlert('Could not open this service request. Check browser storage and retry.');
        }
    };

    const openClientRemediation = (risk: RiskEntry) => {
        const module = linkedSrmRemediationModule(risk, allModules);
        if (module && moduleData(module)) {
            setReviewModuleId(module.id);
            return;
        }
        setRemediationRisk(risk);
    };

    const handleConcludeProject = async () => {
        if (readOnly || !project?.id || !db) return;
        if (!await confirmApp('Conclude this framework? This will lock it and generate a Conclusion Report PDF.')) return;
        setIsConcluding(true);
        const now = new Date().toISOString();
        const conclusionLog = {
            id: `log-conclude-${Date.now()}`,
            timestamp: now,
            user: auth.currentUser?.email || 'System',
            action: 'Project Concluded',
            details: `Framework compliance at ${complianceScore}%. Formally concluded by ${auth.currentUser?.email || 'operator'}.`
        };
        try {
            const currentLogs = (project as any).auditLog || [];
            const updatedLogs = [...currentLogs, conclusionLog];
            await updateDoc(doc(db, 'srm_projects', project.id), {
                status: 'Concluded',
                concludedAt: now,
                auditLog: updatedLogs
            });

            // Build the concluded project snapshot for the report
            const concludedProject = {
                ...project,
                status: 'Concluded',
                concludedAt: now,
                auditLog: updatedLogs
            } as any;

            // Save structured record to generated_reports
            if (db) {
                const reportRecord = {
                    reportType: 'SrmConclusionReport',
                    projectId: project.id,
                    projectName: project.name || project.projectName || 'Untitled',
                    organizationId: project.organizationId,
                    generatedAt: now,
                    generatedBy: auth.currentUser?.email || 'System',
                    complianceAtConclusion: complianceScore,
                    activeRisks: (project.risks || []).filter((r: any) => r.status !== 'Pending' && r.status !== 'Discarded').length,
                    modulesConfigured: (project.modules || []).filter((m: any) => m.status === 'Ready' || m.status === 'Active').length,
                    totalModules: (project.modules || []).length,
                    retentionYears: 1,
                };
                await setDoc(doc(db, 'generated_reports', `srm-conclusion-${project.id}-${Date.now()}`), reportRecord);
            }

            // Auto-download PDF conclusion report
            try { generateSrmConclusionReport(concludedProject); } catch (pdfErr) {
                console.warn('[SRM] PDF generation failed (non-fatal):', pdfErr);
            }

            setProject(prev => prev ? { ...prev, status: 'Concluded' as any } : null);
        } catch (e) {
            console.error('Failed to conclude project:', e);
            void showAppAlert('Failed to conclude project. Please try again.');
        } finally {
            setIsConcluding(false);
        }
    };

    const handleArchiveProject = async () => {
        if (readOnly || !project?.id || !db) return;
        setShowArchiveConfirm(false);
        setIsArchiving(true);
        const now = new Date().toISOString();
        const retentionYears = 1;
        const retentionExpiresAt = new Date(
            Date.now() + retentionYears * 365.25 * 24 * 60 * 60 * 1000
        ).toISOString();
        const archiveLog = {
            id: `log-archive-${Date.now()}`,
            timestamp: now,
            user: auth.currentUser?.email || 'System',
            action: 'Framework Archived',
            details: `Framework moved to archive by ${auth.currentUser?.email || 'operator'}. Retention: ${retentionYears} year. Record retained until ${formatLocalDate(retentionExpiresAt)}.`
        };
        try {
            const currentLogs = (project as any).auditLog || [];
            await updateDoc(doc(db, 'srm_projects', project.id), {
                status: 'Archived',
                archivedAt: now,
                archivedBy: auth.currentUser?.email || 'unknown',
                retentionYears,
                retentionExpiresAt,
                auditLog: [...currentLogs, archiveLog]
            });
            // FIX: Also sync the linked active_frameworks record so it does not
            // remain as 'Active' in Firestore after the project is archived.
            if (activeFramework?.id) {
                try {
                    await updateDoc(doc(db, 'active_frameworks', activeFramework.id), {
                        status: 'Archived',
                        archivedAt: now,
                    });
                } catch (fwErr) {
                    console.warn('[SRM] active_frameworks sync on archive failed (non-fatal):', fwErr);
                }
            }
            // Auto-export JSON snapshot as the final archived record
            try {
                const snapshot = { ...project, status: 'Archived', archivedAt: now, auditLog: [...currentLogs, archiveLog] };
                const blob = new Blob([JSON.stringify(snapshot, null, 2)], { type: 'application/json' });
                const url = URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                a.download = `srm-archive-${project.id}-${Date.now()}.json`;
                document.body.appendChild(a);
                a.click();
                document.body.removeChild(a);
                URL.revokeObjectURL(url);
            } catch { /* non-fatal */ }
            // SrmCommandCenter onSnapshot will detect Archived status and navigate to list
        } catch (e) {
            console.error('Failed to archive project:', e);
            void showAppAlert('Archive failed. Please try again.');
        } finally {
            setIsArchiving(false);
        }
    };
    const handleApproveRisk = async (riskId: string) => {
        if (readOnly)
            return;
        const updatedRisks = (project?.risks || []).map(r => 
            r.id === riskId ? { ...r, status: 'Active' as any } : r
        );
        
        const approvedRisk = updatedRisks.find(r => r.id === riskId);
        const newLog = {
            id: `log-${Date.now()}`,
            timestamp: new Date().toISOString(),
            user: auth.currentUser?.email || 'System',
            action: 'Risk Approved',
            details: `Intel-detected threat "${approvedRisk?.threat}" has been approved and added to active risk register.`
        };

        try {
            if (db && project?.id) {
                const currentLogs = (project as any).auditLog || [];
                await updateDoc(doc(db, 'srm_projects', project.id), {
                    auditLog: [...currentLogs, newLog]
                });
            }
            handleUpdateRisks(updatedRisks);
        } catch (e) {
            console.error("Failed to approve risk:", e);
        }
    };

    const handleDiscardRisk = async (riskId: string) => {
        if (readOnly)
            return;
        const updatedRisks = (project?.risks || []).filter(r => r.id !== riskId);
        handleUpdateRisks(updatedRisks);
    };

    const rawReviewModule = allModules.find(m => m.id === reviewModuleId);
    const isGeopoliticalModule = !isSrmAutomatedWorkflow(rawReviewModule?.workflowId || '') && (rawReviewModule?.workflowId === 'country_geopolitical_analysis' ||
                                 (rawReviewModule?.title || '').toLowerCase().includes('geopolitical') ||
                                 (rawReviewModule?.title || '').toLowerCase().includes('threat analysis'));

    const reviewModule: SrmModule | undefined = (!readOnly && isGeopoliticalModule && latestIntelligence && rawReviewModule)
        ? ({ 
            ...rawReviewModule, 
            status: rawReviewModule.status as SrmModule['status'],
            resultData: { 
                ...latestIntelligence,
                ...(latestIntelligence.resultData || {}), 
                reportType: 'GeopoliticalAnalysis' as const,
                localizedAnalysis: !!latestIntelligence.targetAddress || latestIntelligence.isLocal
            } 
          } as SrmModule)
        : rawReviewModule ? ({ ...rawReviewModule, resultData: moduleData(rawReviewModule) } as SrmModule) : undefined;

    return (
        <div className="flex flex-col gap-8">
            {/* PROJECT HEADER */}
            <div className="bg-white border border-slate-200 rounded-3xl p-8 shadow-sm relative overflow-hidden">
                <div className="absolute top-0 left-0 w-2 h-full bg-blue-600"></div>
                <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-6">
                    <div>
                        <div className="flex items-center gap-3 mb-2">
                            <span className="bg-blue-100 text-blue-700 text-[10px] font-black px-2 py-0.5 rounded tracking-widest uppercase">{project.projectNumber || 'SRM-PROJECT'}</span>
                            <span className={`text-[10px] font-black uppercase px-2 py-0.5 rounded ${project.status === 'Active' ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'}`}>
                                {project.status} {project.status === 'Active' ? 'MONITORING' : 'MODE'}
                            </span>
                            {readOnly && <span className="rounded bg-indigo-100 px-2 py-0.5 text-[10px] font-black uppercase text-indigo-700">Client access</span>}
                        </div>
                        <h2 className="text-3xl font-black text-slate-900 leading-tight">
                            {project.name || project.projectName}
                            {project.status === 'Active' && (
                                <span className="ml-4 inline-flex items-center px-3 py-1 rounded-full text-[10px] font-black bg-emerald-500 text-white animate-pulse uppercase tracking-widest border border-emerald-400">
                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3 mr-1" viewBox="0 0 20 20" fill="currentColor"><path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z" clipRule="evenodd" /></svg>
                                    Monitoring Mode Active
                                </span>
                            )}
                        </h2>
                        
                        {/* New Strategic Wing for Summary */}
                        <div className="mt-4 bg-slate-50/50 border border-slate-100 rounded-2xl p-6 relative group overflow-hidden max-w-4xl">
                            <div className="absolute top-0 right-0 p-2 opacity-10 group-hover:opacity-30 transition-opacity">
                                <svg xmlns="http://www.w3.org/2000/svg" className="h-12 w-12 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" /></svg>
                            </div>
                            <div 
                                className="srm-summary-markdown text-slate-600 text-sm leading-relaxed"
                                dangerouslySetInnerHTML={{ __html: md.render(cleanSrmSummary(project.summary || '') || 'Initial SRM scope pending validation...') }}
                            />
                        </div>
                    </div>
                    {!readOnly && project.status !== 'Active' && (
                        <div className="flex flex-col items-center gap-4">
                            <button 
                                onClick={handleDeployClick}
                                className="bg-blue-600 text-white font-black py-4 px-10 rounded-2xl hover:bg-blue-700 transition-all shadow-xl shadow-blue-100 uppercase tracking-[0.2em] text-xs whitespace-nowrap active:scale-95"
                            >
                                Finalize & Activate Framework
                            </button>
                            <p className="text-[10px] text-slate-400 font-bold uppercase tracking-widest">Awaiting Strategic Confirmation</p>
                        </div>
                    )}
                    {project.status === 'Active' && (
                        <div className="flex flex-col items-center gap-4">
                            <button 
                                onClick={() => setShowEmailModal(true)}
                                className="bg-white text-blue-600 border-2 border-blue-500 font-black py-4 px-10 rounded-2xl hover:bg-blue-50 transition-all uppercase tracking-[0.2em] text-xs whitespace-nowrap active:scale-95 flex items-center gap-2 shadow-lg shadow-blue-50"
                            >
                                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 00-2-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" /></svg>
                                Generate Brief
                            </button>
                            <div className="flex items-center gap-1.5">
                                <span className="w-1.5 h-1.5 bg-emerald-500 rounded-full animate-ping"></span>
                                <span className="text-[9px] text-emerald-600 font-black uppercase tracking-widest">Real-time Intel Linked</span>
                            </div>
                        </div>
                    )}
                </div>

                {/* TABS NAVIGATION */}
                <div className="flex items-center gap-1 mt-10 border-b border-slate-100">
                    {srmDashboardTabs(readOnly, pendingActions.length).map(tab => (
                        <button
                            key={tab.id}
                            onClick={() => {
                                console.log(`[SRM_DEBUG] Tab Transition: ${activeTab} -> ${tab.id}`);
                                setActiveTab(tab.id);
                            }}
                            className={`px-6 py-4 text-xs font-black uppercase tracking-widest transition-all relative ${activeTab === tab.id ? 'text-blue-600 bg-blue-50/30' : 'text-slate-400 hover:text-slate-600 hover:bg-slate-50'}`}
                        >
                            {tab.label}
                            {activeTab === tab.id && <div className="absolute bottom-0 left-0 w-full h-1 bg-gradient-to-r from-blue-600 to-indigo-600 rounded-t-full"></div>}
                        </button>
                    ))}
                </div>
            </div>

            {/* TAB CONTENT: OVERVIEW */}
            {(activeTab as string) === 'overview' && (
                <div className="animate-fade-in space-y-8">
                    {/* Domain KPI Cards (Executive Summary) */}
                    {readOnly && (readyModules < totalModules || riskScore === undefined) && <div role="status" className="rounded-2xl border border-blue-200 bg-blue-50 p-5 text-sm text-blue-900">
                        <p className="font-bold">Framework setup in progress</p>
                        <p className="mt-2">{readyModules} of {totalModules} assessments are ready. {readyModules < totalModules ? 'Complete the remaining audits and response plans. ' : ''}Generated risk scenarios require review before appearing in your register.</p>
                        <button type="button" onClick={() => setActiveTab('actions')} className="mt-3 font-bold underline">View next steps</button>
                    </div>}
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
                        {[
                            { name: 'Governance', key: 'policy', desc: 'Policy & Compliance' },
                            { name: 'Physical', key: 'facility', desc: 'Site Security' },
                            { name: 'People', key: 'training', desc: 'Training & Insider Threat' },
                            { name: 'Cyber/Info', key: 'cyber', desc: 'Data Protection' },
                        ].map((d, i) => {
                            // Bug 6 fix: use interactiveModules (same source as complianceScore) not `components`
                            const relevant = interactiveModules.filter(c => 
                                (c.title || '').toLowerCase().includes(d.key) || 
                                (c as any).workflowId?.includes(d.key) || 
                                (c as any).type?.includes(d.key)
                            );
                            
                            let statusColor = 'bg-slate-300';
                            if (relevant.length > 0) {
                                const hasCritical = relevant.some(c => ((c as any).riskScore || 0) >= 80 || (c.status as any) === 'Non-Compliant');
                                const hasWarning = relevant.some(c => ((c as any).riskScore || 0) >= 50 || c.status === 'Review Needed');
                                statusColor = hasCritical ? 'bg-red-500' : hasWarning ? 'bg-amber-500' : relevant.every(isModuleReady) ? 'bg-emerald-500' : 'bg-slate-300';
                            }
                            const assessmentStatus = !relevant.length ? 'Not configured' : relevant.every(isModuleReady) ? 'Assessment ready' : 'Assessment pending';

                            return (
                                <div key={i} className="bg-white p-4 rounded-3xl border border-slate-100 shadow-sm flex items-center justify-between group hover:border-blue-200 transition-all">
                                    <div>
                                        <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest">{d.name}</p>
                                        <p className="text-sm font-bold text-slate-700">{d.desc}</p>
                                        <p className="mt-1 text-xs text-slate-500">{assessmentStatus}</p>
                                    </div>
                                    <div className={`w-3 h-3 rounded-full ${statusColor} ring-4 ring-slate-50 group-hover:scale-110 transition-transform`}></div>
                                </div>
                            );
                        })}

                        {/* Overall Risk Score Card */}
                        <div className="bg-white border border-slate-200 p-6 rounded-[2.5rem] shadow-sm flex flex-col justify-center relative overflow-hidden group hover:border-blue-200 transition-all">
                            <div className="absolute top-0 right-0 w-32 h-32 bg-blue-600/5 rounded-full -mr-16 -mt-16 blur-3xl group-hover:bg-blue-600/10 transition-all"></div>
                            <div className="flex justify-between items-start mb-2 relative z-10">
                                <span className="text-[10px] font-bold text-slate-400 uppercase tracking-[0.2em]">Overall Risk Portrait</span>
                                <div className="flex flex-col items-end">
                                    <span className="text-2xl font-black text-slate-900">{riskScore ?? '—'}</span>
                                    <span className="text-[8px] font-bold text-slate-400 uppercase">{riskScore === undefined ? 'Awaiting assessment' : 'Avg L×I'}</span>
                                </div>
                            </div>
                            <div className="mt-4 flex items-center gap-2">
                                <div className="flex-1 h-1.5 bg-slate-100 rounded-full overflow-hidden">
                                    <div 
                                        className={`h-full transition-all duration-1000 ${
                                            riskScore === undefined ? 'bg-slate-300' : riskScore >= 15 ? 'bg-red-500' :
                                            riskScore >= 10 ? 'bg-amber-500' : 'bg-emerald-500'
                                        }`} 
                                        style={{ width: `${Math.min(100, ((riskScore ?? 0) / 25) * 100)}%` }}
                                    ></div>
                                </div>
                                <span className={`text-[10px] font-bold uppercase ${
                                    riskScore === undefined ? 'text-slate-500' : riskScore >= 15 ? 'text-red-500' :
                                    riskScore >= 10 ? 'text-amber-500' : 'text-emerald-500'
                                }`}>
                                    {riskScore === undefined ? 'Pending' : riskScore >= 15 ? 'Critical' : riskScore >= 10 ? 'Elevated' : 'Nominal'}
                                </span>
                            </div>
                        </div>
                    </div>

                    <div className="space-y-8">
                        {/* TOP ROW: EXECUTIVE METRICS */}
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                            {/* Compliance Card */}
                            <div className="bg-white border border-slate-200 rounded-3xl p-6 shadow-sm flex flex-col justify-center">
                                <div className="flex justify-between items-center mb-6">
                                    <h3 className="text-xs font-black text-slate-400 uppercase tracking-widest">Framework Readiness</h3>
                                    <span className="text-2xl font-black text-blue-600">{complianceScore}%</span>
                                </div>
                                <div className="h-4 bg-slate-100 rounded-full overflow-hidden mb-4">
                                    <div className="bg-blue-600 h-full transition-all duration-1000 ease-out" style={{ width: `${complianceScore}%` }}></div>
                                </div>
                                <p className="text-[10px] text-slate-500 font-bold uppercase text-center">{readyModules} of {totalModules} Assessments Ready</p>
                            </div>

                            {/* High Priority Actions */}
                            <div className="bg-white border border-slate-200 rounded-3xl p-6 shadow-sm">
                                <h3 className="text-xs font-black text-slate-400 uppercase tracking-widest mb-4">Strategic Critical Actions</h3>
                                <div className="max-h-40 overflow-y-auto custom-scrollbar pr-2">
                                    <div className="space-y-4">
                                        {criticalActions.length > 0 ? criticalActions.map((action, i) => (
                                            <div key={i} className="p-3 bg-red-50 border border-red-100 rounded-xl flex justify-between items-center">
                                                <div>
                                                    <p className="text-xs font-bold text-red-800">{action.task}</p>
                                                    <p className="text-[10px] text-red-600 mt-1 uppercase font-black">{action.componentName}</p>
                                                </div>
                                                <span className="bg-red-200 text-red-700 text-[8px] font-black px-1.5 py-0.5 rounded tracking-tighter uppercase">High Priority</span>
                                            </div>
                                        )) : (
                                            <div className="py-8 text-center">
                                                <div className="w-12 h-12 bg-emerald-50 text-emerald-500 rounded-full flex items-center justify-center mx-auto mb-3">
                                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg>
                                                </div>
                                                <p className="text-xs font-bold text-slate-500">No critical actions pending.</p>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* MIDDLE ROW: FULL-WIDTH RISK REGISTER */}
                        <div id="srm-risk-register" className="w-full">
                            <SrmRiskRegister 
                                project={project} 
                                alerts={alerts} 
                                readOnly={readOnly}
                                onUpdate={readOnly ? undefined : handleUpdateRisks}
                                onUpdateLifecycle={readOnly && !isClientView ? handleClientRiskLifecycle : undefined}
                                onOpenRemediation={readOnly ? openClientRemediation : undefined}
                                onLaunchTool={readOnly ? undefined : (recommendationTag: string) => {
                                    // Resolve the recommended module from title keyword match
                                    const tag = recommendationTag.toLowerCase();
                                    const targetModule = allModules.find(m => {
                                        const title = (m.title || '').toLowerCase();
                                        const wfId = (m.workflowId || '').toLowerCase();
                                        return title.includes('fsat') && tag.includes('fsat') ||
                                               title.includes('ep threat') && tag.includes('ep threat') ||
                                               title.includes('crisis') && tag.includes('crisis') ||
                                               title.includes('geopolitical') && tag.includes('geopolitical') ||
                                               title.includes('information highway') && tag.includes('information highway') ||
                                               title.includes('business resiliency') && tag.includes('business resiliency') ||
                                               title.includes('emergency') && tag.includes('emergency') ||
                                               tag.includes(title.substring(0, 8)) ||
                                               wfId.includes(tag.substring(0, 8));
                                    });
                                    if (targetModule) {
                                        handleRerunTool(targetModule);
                                    } else {
                                        // Fallback: navigate to Systems & Tools tab so user can pick manually
                                        console.log(`[SRM_DEBUG] Launch Tool Fallback: No specific module found for "${recommendationTag}", navigating to components tab.`);
                                        setActiveTab('components');
                                    }
                                }}
                            />

                        </div>
                    
                        {/* BOTTOM ROW: BACKGROUND INTELLIGENCE */}
                        {backgroundModules.length > 0 && (
                            <div className="bg-white border border-slate-200 rounded-3xl p-6 shadow-sm w-full">
                                <div className="flex items-center justify-between mb-4">
                                    <h3 className="text-xs font-black text-slate-400 uppercase tracking-widest">Integrated Strategic Intelligence</h3>
                                    <span className="text-[10px] text-slate-400 font-bold italic">{readOnly ? 'Delivered analysis remains consultant managed' : 'Approve each analysis below to stage risk scenarios into the register'}</span>
                                </div>
                                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                                    {backgroundModules.map(m => (
                                        <div key={m.id} className="p-4 bg-slate-50 rounded-2xl border border-slate-100 hover:border-blue-200 transition-colors group cursor-pointer" onClick={() => setReviewModuleId(m.id)}>
                                            <div className="flex items-center gap-3 mb-2">
                                                <div className="p-2 bg-white rounded-lg group-hover:text-blue-600 transition-colors">
                                                    {(SUITE_DETAILS[m.suiteId]?.icon || ICONS.folder) ? 
                                                        React.cloneElement((SUITE_DETAILS[m.suiteId]?.icon || ICONS.folder) as any, { className: 'h-4 w-4' }) 
                                                        : null
                                                    }
                                                </div>
                                                <span className="text-xs font-bold text-slate-800">{m.title}</span>
                                            </div>
                                            <div className="flex justify-between items-center">
                                                <span className="text-[10px] text-slate-400 font-bold uppercase">{m.status}</span>
                                                <span className="text-blue-600 text-[10px] font-black uppercase group-hover:underline flex items-center gap-1">
                                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" /></svg>
                                                    View Report
                                                </span>
                                                {canRegenerate && isSrmAutomatedWorkflow(m.workflowId) && <button type="button"
                                                    onClick={event => { event.stopPropagation(); void regenerateThreatAnalysis(m); }}
                                                    disabled={!!regeneratingModuleId || isRegenerating(m)}
                                                    className="rounded-lg bg-blue-600 px-3 py-2 text-xs font-bold text-white hover:bg-blue-700 disabled:bg-slate-400">
                                                    {isRegenerating(m) ? 'Regenerating…' : 'Regenerate'}
                                                </button>}
                                            </div>
                                            {srmModuleReportGaps({ ...m, resultData: moduleData(m) }).map(gap =>
                                                <p key={gap} role="status" className="mt-3 text-xs text-amber-700">{gap}</p>)}
                                        </div>
                                    ))}
                                </div>
                                {canRegenerate && regenerationError && <p role="alert" className="mt-4 text-sm text-red-700">{regenerationError}</p>}
                            </div>
                        )}
                    </div>

                    {/* STRATEGIC SECURITY NEWS GRID (Implemented from Workflow Diagram) */}
                    <div className="mt-8">
                        <div className="flex items-center justify-between mb-6">
                            <div className="flex items-center gap-3">
                                <div className="w-1.5 h-6 bg-red-600 rounded-full shadow-[0_0_8px_rgba(220,38,38,0.5)]"></div>
                                <h3 className="text-sm font-black text-slate-800 uppercase tracking-widest">ECC Global Command Center: Live Intelligence</h3>
                            </div>
                            <span className="flex items-center gap-2 text-[10px] font-black text-red-600 uppercase tracking-widest bg-red-50 px-4 py-1.5 rounded-full border border-red-100 shadow-sm">
                                <span className="relative flex h-2 w-2">
                                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75"></span>
                                    <span className="relative inline-flex rounded-full h-2 w-2 bg-red-600"></span>
                                </span>
                                Real-time OSINT & Live Broadcast Hub
                            </span>
                        </div>
                        <div className="bg-white border border-slate-200 p-6 rounded-[2.5rem] shadow-sm overflow-hidden relative">
                            <GlobalLiveNewsGrid maxFeeds={8} layout="horizontal" coverageCountries={organizationCoverageCountries(organization)} />

                        </div>
                    </div>
                </div>
            )}

            {/* TAB CONTENT: ACTIONS */}
            {(activeTab as string) === 'actions' && (
                <div className="animate-fade-in space-y-6">
                    <div className="bg-white border border-slate-200 rounded-3xl shadow-sm overflow-hidden">
                        <div className="p-6 border-b border-slate-100 bg-slate-50/50 flex justify-between items-center">
                            <h3 className="font-black text-slate-900 uppercase tracking-tight">Mission Action Registry</h3>
                            <span className="text-[10px] font-black text-slate-400 uppercase">{pendingActions.length} Items Outstanding</span>
                        </div>
                        <div className="divide-y divide-slate-100">
                            {allActions.length > 0 ? allActions.map((action, i) => {
                                const act = action as any;
                                const isRiskAction = !!act.isRisk;
                                const hasTargetModule = isRiskAction && act.componentId && act.componentId !== 'srm_risk_register';
                                return (
                                <div key={i} className="p-6 flex items-start justify-between hover:bg-slate-50 transition-colors">
                                    <div className="flex gap-4">
                                        <div className={`mt-1 h-3 w-3 rounded-full flex-shrink-0 ${action.priority === 'Critical' ? 'bg-red-500 animate-pulse' : (action.priority === 'High' ? 'bg-amber-500' : 'bg-blue-500')}`}></div>
                                        <div>
                                            <p className="font-bold text-slate-900">{action.task}</p>
                                            <p className="text-[10px] text-slate-400 uppercase tracking-wider">{action.componentName}</p>
                                            <div className="flex items-center gap-3 mt-3">
                                                <span className={`text-[9px] font-black px-2 py-0.5 rounded uppercase ${isRiskAction ? 'bg-red-50 text-red-600 border border-red-100' : 'bg-slate-100 text-slate-600'}`}>{isRiskAction ? '⚠ Risk Item' : action.componentName}</span>
                                                <span className={`text-[9px] font-black px-2 py-0.5 rounded uppercase ${action.status === 'complete' ? 'bg-emerald-100 text-emerald-700' : action.status === 'in-progress' ? 'bg-blue-100 text-blue-700' : 'bg-amber-100 text-amber-700'}`}>{action.status}</span>
                                            </div>
                                        </div>
                                    </div>
                                    {readOnly ? (isRiskAction ? <button type="button" onClick={() => {
                                        setActiveTab('overview');
                                        window.setTimeout(() => document.getElementById(`srm-risk-${act.riskId}`)?.scrollIntoView({ behavior: 'smooth', block: 'center' }), 50);
                                    }} className="rounded-lg border border-blue-100 px-4 py-2 text-[10px] font-black uppercase text-blue-700 hover:bg-blue-50">Open Risk</button>
                                        : <button type="button" onClick={() => requestModuleService(allModules.find(module => module.id === action.componentId)
                                            || activeFramework?.components.find(component => component.id === action.componentId), action.task)}
                                            className="rounded-lg border border-blue-100 px-4 py-2 text-[10px] font-black uppercase text-blue-700 hover:bg-blue-50">Request Service</button>) : <button
                                        onClick={() => {
                                            console.log(`[SRM_DEBUG] Action Registry Item Clicked:`, {
                                                task: action.task,
                                                componentId: action.componentId,
                                                isRisk: act.isRisk,
                                                isProjectModule: act.isProjectModule
                                            });

                                            if (act.isProjectModule) {
                                                const mod = allModules.find(m => m.id === action.componentId);
                                                if (mod) {
                                                    console.log(`[SRM_DEBUG] Rerunning project module:`, mod.title);
                                                    handleRerunTool(mod);
                                                } else {
                                                    console.warn(`[SRM_DEBUG] Could not find module with ID:`, action.componentId);
                                                }
                                            } else if (isRiskAction) {
                                                if (hasTargetModule) {
                                                    const mod = allModules.find(m => m.id === act.componentId);
                                                    if (mod) {
                                                        console.log(`[SRM_DEBUG] Rerunning risk-associated tool:`, mod.title);
                                                        handleRerunTool(mod);
                                                    } else {
                                                        console.warn(`[SRM_DEBUG] Could not find risk-associated module:`, act.componentId);
                                                    }
                                                } else {
                                                    console.log(`[SRM_DEBUG] Action Registry: Navigating to Overview to show Risk Register`);
                                                    setActiveTab('overview');
                                                    // Use a slightly longer timeout and a check to ensure DOM availability
                                                    let attempts = 0;
                                                    const scrollInterval = setInterval(() => {
                                                        const el = document.getElementById('srm-risk-register');
                                                        if (el) {
                                                            console.log(`[SRM_DEBUG] Risk Register DOM element found, scrolling...`);
                                                            el.scrollIntoView({ behavior: 'smooth', block: 'start' });
                                                            clearInterval(scrollInterval);
                                                        } else if (attempts > 10) {
                                                            console.error(`[SRM_DEBUG] Risk Register DOM element NOT FOUND after 1s`);
                                                            clearInterval(scrollInterval);
                                                        }
                                                        attempts++;
                                                    }, 100);
                                                }
                                            } else {
                                                const comp = activeFramework?.components.find(c => c.id === action.componentId);
                                                if (comp) {
                                                    console.log(`[SRM_DEBUG] Rerunning framework component:`, comp.title);
                                                    handleRerunTool(comp);
                                                } else {
                                                    console.warn(`[SRM_DEBUG] Could not find component with ID:`, action.componentId);
                                                }
                                            }
                                        }}
                                        className="px-4 py-2 text-[10px] font-black uppercase whitespace-nowrap text-blue-600 hover:bg-blue-50 rounded-lg border border-transparent hover:border-blue-100 transition-all tracking-tighter"
                                    >
                                        {isRiskAction && !hasTargetModule ? 'View Risk Register' : 'Execute Action'}
                                    </button>}
                                </div>
                                );
                            }) : (
                                <div className="p-12 text-center text-slate-400 font-bold uppercase text-xs tracking-widest">No Action Items Found</div>
                            )}
                        </div>
                    </div>
                </div>
            )}

            {/* TAB CONTENT: COMPONENTS */}
            {(activeTab as string) === 'components' && (
                <div className="animate-fade-in grid grid-cols-1 gap-4">
                    {canRegenerate && regenerationError && <p role="alert" className="text-sm text-red-700">{regenerationError}</p>}
                    {/* B3 FIX: Always render from allModules (srm_projects source of truth), not activeFramework.components */}
                    {allModules.filter(m => !BACKGROUND_TOOLS.includes(m.workflowId || '')).map((module) => {
                        const suiteInfo = SUITE_DETAILS[module.suiteId] || { color: { bg: 'bg-gray-100', text: 'text-gray-600' }, icon: ICONS.folder };
                        const needsReview = module.status === 'Review Needed' || module.status === 'Review';
                        const data = moduleData(module);
                        return (
                            <div key={module.id} className={`bg-white border rounded-3xl p-8 shadow-sm flex flex-col md:flex-row items-center justify-between gap-6 hover:shadow-md transition-all ${needsReview ? 'border-amber-400 ring-4 ring-amber-50' : 'border-slate-200'}`}>
                                <div className="flex items-start gap-6 flex-1">
                                    <div className={`p-5 rounded-2xl ${suiteInfo.color.bg} ${suiteInfo.color.text} shadow-inner`}>
                                        {React.cloneElement(suiteInfo.icon as any, { className: 'h-8 w-8' })}
                                    </div>
                                    <div>
                                        <h3 className="font-black text-xl text-slate-900">{module.title}</h3>
                                        <p className="text-slate-500 text-sm mt-1 max-w-xl">{(module as any).description || (module as any).summary || 'Operational security component'}</p>
                                        {srmModuleReportGaps({ ...module, resultData: data }).map(gap =>
                                            <p key={gap} role="status" className="mt-3 text-xs text-amber-700">{gap}</p>)}
                                        <div className="flex gap-2 mt-4">
                                            <span className={`text-[9px] font-black uppercase px-2 py-1 rounded ${module.status === 'Ready' || module.status === 'Active' ? 'bg-emerald-100 text-emerald-800' : (needsReview ? 'bg-amber-100 text-amber-800' : 'bg-slate-100 text-slate-600')}`}>
                                                {module.status}
                                            </span>
                                            {data && <span className="text-[9px] font-black uppercase px-2 py-1 rounded bg-blue-50 text-blue-600 border border-blue-100">Live Simulation Data</span>}
                                        </div>
                                    </div>
                                </div>
                                <div className="flex items-center gap-4">
                                    <button 
                                        onClick={() => data ? setReviewModuleId(module.id) : readOnly ? requestModuleService(module) : handleRerunTool(module)}
                                        className={`px-8 py-3 rounded-2xl font-black text-xs uppercase tracking-widest transition-all ${module.status === 'Ready' || module.status === 'Active' ? 'bg-slate-100 text-slate-700 hover:bg-slate-200' : 'bg-blue-600 text-white hover:bg-blue-700 shadow-lg shadow-blue-100'}`}
                                    >
                                        {readOnly ? (data ? 'View Module' : 'Request Service') : data ? 'Open Module' : 'Configure Tool'}
                                    </button>
                                    {canRegenerate && isSrmAutomatedWorkflow(module.workflowId) && <button type="button"
                                        onClick={() => void regenerateThreatAnalysis(module)} disabled={!!regeneratingModuleId || isRegenerating(module)}
                                        className="rounded-lg bg-blue-600 px-3 py-2 text-xs font-bold text-white disabled:bg-slate-400">
                                        {isRegenerating(module) ? 'Regenerating…' : 'Regenerate'}
                                    </button>}
                                    {!readOnly && <button onClick={() => handleDeleteModule(module.id)} className="p-3 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-all"><svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg></button>}
                                </div>
                            </div>
                        );
                    })}
                    {!readOnly && <button onClick={() => onNavigate({ page: 'knowledgeHub' })} className="border-2 border-dashed border-slate-200 p-12 rounded-3xl flex flex-col items-center justify-center text-slate-400 hover:text-blue-600 hover:border-blue-300 hover:bg-blue-50 transition-all font-black uppercase text-xs tracking-widest group">
                        <div className="p-4 bg-white rounded-2xl border border-slate-100 mb-4 group-hover:scale-110 transition-transform"><svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" /></svg></div>
                        Provision New Strategic Component
                    </button>}
                </div>
            )}

            {/* TAB CONTENT: AUDIT LOG */}
            {!readOnly && (activeTab as string) === 'audit_log' && (
                <div className="animate-fade-in space-y-6">

                    {/* ── Conclusion CTA ── shown when project is ready to close */}
                    {(() => {
                        const allMitigated = (project.risks || []).filter(r => r.status !== 'Pending').every(r => r.status === 'Mitigated' || r.status === 'Closed');
                        const hasMitigatedRisks = (project.risks || []).filter(r => r.status === 'Mitigated' || r.status === 'Closed').length > 0;
                        const isEligible = complianceScore >= 90 && allMitigated && hasMitigatedRisks && project.status === 'Active';
                        if (!isEligible) return null;
                        return (
                            <div className="bg-gradient-to-r from-emerald-600 to-teal-600 rounded-3xl p-8 text-white shadow-xl shadow-emerald-100 relative overflow-hidden">
                                <div className="absolute top-0 right-0 w-48 h-48 bg-white/5 rounded-full -mr-16 -mt-16 blur-3xl" />
                                <div className="relative z-10 flex flex-col md:flex-row items-center justify-between gap-6">
                                    <div className="flex items-center gap-5">
                                        <div className="w-16 h-16 bg-white/20 rounded-2xl flex items-center justify-center flex-shrink-0">
                                            <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z" /></svg>
                                        </div>
                                        <div>
                                            <h3 className="text-xl font-black tracking-tight">Framework Objectives Achieved</h3>
                                            <p className="text-emerald-100 text-sm mt-1">Compliance at <strong>{complianceScore}%</strong> · All active risks mitigated · Ready for formal conclusion</p>
                                        </div>
                                    </div>
                                    <button
                                        onClick={handleConcludeProject}
                                        disabled={isConcluding}
                                        className="flex-shrink-0 px-10 py-4 bg-white text-emerald-700 font-black text-xs uppercase tracking-widest rounded-2xl hover:bg-emerald-50 transition-all shadow-xl active:scale-95 disabled:opacity-60"
                                    >
                                        {isConcluding ? 'Concluding...' : 'Conclude & Archive Project'}
                                    </button>
                                </div>
                            </div>
                        );
                    })()}

                    {/* Concluded badge */}
                    {(project as any).status === 'Concluded' && (
                        <div className="bg-slate-800 text-white rounded-3xl p-8 flex items-center justify-between gap-5 shadow-xl">
                            <div className="flex items-center gap-5">
                                <div className="w-14 h-14 bg-emerald-500/20 rounded-2xl flex items-center justify-center flex-shrink-0">
                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-7 w-7 text-emerald-400" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg>
                                </div>
                                <div>
                                    <h3 className="text-lg font-black text-white tracking-tight">Project Concluded & Archived</h3>
                                    <p className="text-slate-400 text-sm mt-1">This framework has been formally concluded. All records are preserved below.</p>
                                </div>
                            </div>
                            <div className="flex items-center gap-3 flex-shrink-0">
                                <button
                                    onClick={() => setShowArchiveConfirm(true)}
                                    disabled={isArchiving}
                                    className="px-5 py-3 bg-amber-500 hover:bg-amber-400 disabled:bg-slate-600 text-white font-black text-xs uppercase tracking-widest rounded-2xl transition-all shadow-lg active:scale-95 flex items-center gap-2"
                                >
                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 8h14M5 8a2 2 0 110-4h14a2 2 0 110 4M5 8l1 12a2 2 0 002 2h8a2 2 0 002-2L19 8" />
                                    </svg>
                                    {isArchiving ? 'Archiving…' : 'Archive'}
                                </button>
                                <button
                                    onClick={() => setShowDecommissionModal(true)}
                                    className="px-5 py-3 bg-red-700 hover:bg-red-600 text-white font-black text-xs uppercase tracking-widest rounded-2xl transition-all shadow-lg active:scale-95 flex items-center gap-2"
                                    title="Permanently decommission and delete this framework and all associated data"
                                >
                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                    </svg>
                                    Decommission
                                </button>
                            </div>
                        </div>
                    )}

                    {/* Archive confirmation modal */}
                    {showArchiveConfirm && (
                        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 animate-fade-in">
                            <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-8 border border-slate-200">
                                <div className="flex items-center gap-3 mb-5">
                                    <div className="p-3 bg-amber-50 rounded-xl">
                                        <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-amber-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 8h14M5 8a2 2 0 110-4h14a2 2 0 110 4M5 8l1 12a2 2 0 002 2h8a2 2 0 002-2L19 8" />
                                        </svg>
                                    </div>
                                    <h3 className="text-lg font-black text-slate-900">Archive This Framework?</h3>
                                </div>
                                <p className="text-sm text-slate-600 mb-4">
                                    <strong className="text-slate-800">{project.name || project.projectName}</strong> will be moved to read-only archive storage. A JSON snapshot will be downloaded automatically. You can view it from the archive section. To permanently remove all data, use <strong>Decommission</strong> instead.
                                </p>
                                <div className="bg-amber-50 border border-amber-200 rounded-xl px-4 py-3 mb-5">
                                    <p className="text-[11px] font-bold text-amber-700 uppercase tracking-widest mb-1">Compliance Notice</p>
                                    <p className="text-xs text-amber-700">
                                        This record will be retained for <strong>1 year</strong>. Your organisation's legal counsel should confirm this meets applicable regulatory requirements. Many standards (NIST, FISMA) require 3-7 years.
                                    </p>
                                </div>
                                <div className="flex gap-3">
                                    <button
                                        onClick={() => setShowArchiveConfirm(false)}
                                        className="flex-1 py-3 rounded-xl border border-slate-200 text-slate-600 text-sm font-bold hover:bg-slate-50 transition-colors"
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        onClick={handleArchiveProject}
                                        className="flex-1 py-3 rounded-xl bg-amber-500 hover:bg-amber-600 text-white text-sm font-bold transition-colors"
                                    >
                                        Archive Framework
                                    </button>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Structured Decommission Modal */}
                    {showDecommissionModal && project && (
                        <FrameworkDecommissionModal
                            isOpen={showDecommissionModal}
                            project={project}
                            organization={organization}
                            onClose={() => setShowDecommissionModal(false)}
                            onComplete={() => {
                                setShowDecommissionModal(false);
                                onBack();
                            }}
                        />
                    )}

                    {/* Audit Log Card */}
                    <div className="bg-white border border-slate-200 rounded-3xl shadow-sm overflow-hidden">
                        <div className="p-8 bg-slate-50/50 border-b border-slate-200">
                            <h3 className="font-black text-slate-900 uppercase tracking-tight flex items-center gap-3">
                                <div className="p-2 bg-blue-600 rounded-lg text-white">
                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" /></svg>
                                </div>
                                Mission Audit Sequence
                            </h3>
                        </div>
                        <div className="p-6 divide-y divide-slate-100">
                            {(() => {
                                const logs = [
                                    ...((project as any).auditLog || []),
                                    ...(activeFramework?.auditLog || [])
                                ];
                                if (logs.length === 0) return <div className="p-12 text-center text-slate-400 font-bold uppercase text-xs tracking-widest">No Audit Logs Initialized</div>;
                                
                                return logs.sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime()).map((entry: any) => (
                                    <div key={entry.id} className="py-6 flex items-start gap-6 hover:bg-slate-50/50 transition-colors px-4 rounded-2xl">
                                        <div className={`mt-1.5 w-1.5 h-1.5 rounded-full flex-shrink-0 ${
                                            (entry.action || '').toLowerCase().includes('concluded') ? 'bg-emerald-500 shadow-[0_0_8px_rgba(16,185,129,0.8)]' :
                                            (entry.action || '').toLowerCase().includes('deployed') || (entry.action || '').toLowerCase().includes('completed') ? 'bg-blue-500 shadow-[0_0_8px_rgba(37,99,235,0.6)]' : 'bg-slate-400'
                                        }`} />
                                        <div className="flex-1">
                                            <div className="flex items-center justify-between">
                                                <span className="font-black text-xs text-slate-900 uppercase tracking-tight">{entry.action}</span>
                                                <span className="text-[10px] text-slate-400 font-mono">{formatLocalDateTime(entry.timestamp)}</span>
                                            </div>
                                            <p className="text-sm text-slate-500 mt-1 italic">{entry.details}</p>
                                            <p className="text-[9px] text-blue-600 mt-2 font-black uppercase tracking-[0.2em]">{entry.user}</p>
                                        </div>
                                    </div>
                                ));
                            })()}
                        </div>
                    </div>
                </div>
            )}

            {/* MODALS */}
            {remediationRisk && <div className="fixed inset-0 z-[100] flex items-center justify-center bg-slate-950/70 p-4">
                <div role="dialog" aria-modal="true" aria-label="Remediation path" className="w-full max-w-xl rounded-2xl bg-white p-6 shadow-2xl">
                    <div className="flex items-start justify-between gap-4"><div><p className="text-xs font-black uppercase tracking-wider text-blue-700">Remediation path</p>
                        <h3 className="mt-1 text-xl font-black text-slate-900">{remediationRisk.threat}</h3></div>
                        <button type="button" onClick={() => setRemediationRisk(null)} className="rounded-lg border border-slate-200 px-3 py-2 text-sm font-bold text-slate-700">Close</button></div>
                    <p className="mt-5 text-sm font-semibold text-slate-800">{remediationRisk.recommendation || 'No linked report is available.'}</p>
                    <p className="mt-3 text-sm leading-6 text-slate-600">{remediationRisk.mitigation}</p>
                    <p className="mt-5 rounded-lg bg-amber-50 p-3 text-xs font-semibold text-amber-800">No linked module report is available yet. Your consultant manages this path.</p>
                </div>
            </div>}
            {showEmailModal && <EmailReportModal project={project} activeFramework={activeFramework} onClose={() => setShowEmailModal(false)} />}
            {reviewModuleId && reviewModule && (
                <SrmModuleReviewModal 
                    isOpen={!!reviewModuleId}
                    onClose={() => setReviewModuleId(null)}
                    onApprove={async () => {
                        console.log(`[SRM_DEBUG] SrmModuleReviewModal onApprove triggered for:`, reviewModuleId);
                        if (onUpdateModule) {
                            const risksBefore = (project.risks || []).length;
                            console.log(`[SRM_DEBUG] Invoking onUpdateModule for:`, reviewModuleId);
                            await onUpdateModule(project.id, reviewModuleId, reviewModule.resultData);
                            console.log(`[SRM_DEBUG] onUpdateModule completed`);
                            setReviewModuleId(null);
                            // Bug 4 fix: show toast so user knows pending risks were staged
                            const risksAfter = (project.risks || []).length;
                            const newCount = Math.max(0, risksAfter - risksBefore);
                            console.log(`[SRM_DEBUG] Module updated. Risks staged:`, newCount);
                            setApprovalToast({ count: newCount > 0 ? newCount : 1 });
                            setTimeout(() => setApprovalToast(null), 5000);
                        } else {
                            console.warn(`[SRM_DEBUG] onUpdateModule callback missing`);
                        }
                    }}
                    moduleName={reviewModule.title || 'Strategic Analysis'}
                    resultData={reviewModule.resultData}
                    workflowId={reviewModule.workflowId}
                    organizationId={organization?.id || project.organizationId}
                    srmProject={project}
                    srmModule={reviewModule}
                    readOnly={readOnly}
                    onRegenerate={canRegenerate && isSrmAutomatedWorkflow(reviewModule.workflowId) ? () => regenerateThreatAnalysis(reviewModule) : undefined}
                    reportGaps={srmModuleReportGaps(reviewModule)}
                    isRegenerating={isRegenerating(reviewModule)}
                    regenerationError={regenerationError}
                />
            )}

            {/* Bug 4: Post-approval toast notification */}
            {!readOnly && approvalToast && (
                <div className="fixed bottom-8 right-8 z-[200] animate-in slide-in-from-bottom-4 duration-500">
                    <div className="bg-slate-900 text-white px-6 py-4 rounded-2xl shadow-2xl shadow-slate-900/40 flex items-center gap-4 border border-slate-700 max-w-sm">
                        <div className="w-10 h-10 bg-blue-500/20 rounded-xl flex items-center justify-center flex-shrink-0">
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-blue-400" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                        </div>
                        <div>
                            <p className="text-sm font-black text-white">Analysis Approved</p>
                            <p className="text-xs text-slate-400 mt-0.5">New risk scenarios have been staged in the Active Risk Register for your review.</p>
                        </div>
                        <button onClick={() => setApprovalToast(null)} className="ml-2 text-slate-500 hover:text-white transition-colors flex-shrink-0">
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
};

export default SrmProjectDashboard;
